import type { AdminBrand, CloudResult, StaffProfile } from './types';

const API_BASE = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'chaotu_admin_token';
const CONTROL_TOKEN_KEY = 'chaotu_control_token';
const SYSTEM_ID_KEY = 'chaotu_system_id';

export type AssetType = 'wallpapers/original' | 'banners' | 'albums' | 'categories' | 'avatars' | 'watermarks' | 'ui' | 'temp' | 'misc';

export function getApiBaseUrl(): string {
  return API_BASE || window.location.origin;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.status = status;
  }
}

export function getToken(): string {
  return sessionStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function setCurrentSystemId(systemId: string): void {
  sessionStorage.setItem(SYSTEM_ID_KEY, systemId || 'official');
}

export function getCurrentSystemId(): string {
  return sessionStorage.getItem(SYSTEM_ID_KEY) || 'official';
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.code !== 0) {
    if (response.status === 401) clearToken();
    throw new ApiError(body.msg || `请求失败（${response.status}）`, response.status);
  }
  return body;
}

export async function staffLogin(credentials: { key: string } | { loginType: 'designer' | 'admin'; username: string; password: string }, systemId = 'official'): Promise<StaffProfile> {
  const response = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...credentials, systemId }),
  });
  const result = await readJson(response);
  setToken(result.data.adminToken);
  setCurrentSystemId(systemId);
  return { ...result.data.user, systemId } as StaffProfile;
}

export async function getStaffProfile(): Promise<StaffProfile> {
  const result = await adminRequest<StaffProfile>('/api/admin/me');
  return { ...result.data, systemId: getCurrentSystemId() };
}

export async function getAdminBrand(systemId = 'official'): Promise<AdminBrand> {
  const response = await fetch(`${API_BASE}/api/admin/brand?systemId=${encodeURIComponent(systemId)}`);
  const result = await readJson(response);
  return result.data as AdminBrand;
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getToken();
  return {
    Authorization: `Bearer ${token}`,
    'X-Admin-Token': `Bearer ${token}`,
    ...extra,
  };
}

export async function cloud<T = unknown>(name: string, data: Record<string, unknown> = {}): Promise<CloudResult<T>> {
  const response = await fetch(`${API_BASE}/api/cloud/${encodeURIComponent(name)}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  });
  return readJson(response);
}

export async function adminRequest<T = unknown>(path: string, options: {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: Record<string, unknown>;
} = {}): Promise<CloudResult<T>> {
  const method = options.method || 'GET';
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(method === 'GET' ? {} : { 'Content-Type': 'application/json' }),
    ...(method === 'GET' ? {} : { body: JSON.stringify(options.data || {}) }),
  });
  return readJson(response);
}

export async function uploadImage(file: File, assetType: AssetType = 'misc'): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  form.append('assetType', assetType);
  form.append('temporary', 'true');
  const endpoint = assetType === 'avatars' ? '/api/admin/avatar/upload' : '/api/storage/upload';
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  const result = await readJson(response);
  return result.data.fileID;
}

export async function uploadWallpaperImage(file: File): Promise<{ fileID: string; thumbnailFileID: string; width: number; height: number; fileSize: number }> {
  const form = new FormData();
  form.append('file', file);
  form.append('assetType', 'wallpapers/original');
  form.append('createThumbnail', 'true');
  form.append('temporary', 'true');
  const response = await fetch(`${API_BASE}/api/storage/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  const result = await readJson(response);
  return {
    fileID: result.data.fileID,
    thumbnailFileID: result.data.thumbnailFileID || '',
    width: Number(result.data.width || 0),
    height: Number(result.data.height || 0),
    fileSize: Number(result.data.fileSize || file.size || 0),
  };
}

export async function generateImageLabels(input: { file?: Blob; imageUrl?: string; tagPool: string[] }): Promise<{ tags: string[]; model: string }> {
  const form = new FormData();
  if (input.file) form.append('file', input.file, 'label-source.jpg');
  if (input.imageUrl) form.append('imageUrl', input.imageUrl);
  form.append('tagPool', JSON.stringify(input.tagPool || []));
  const response = await fetch(`${API_BASE}/api/admin/ai-labeling/generate`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  const result = await readJson(response);
  return result.data;
}

export async function deleteUploadedImages(fileList: string[], keepalive = false): Promise<void> {
  if (!fileList.length) return;
  const response = await fetch(`${API_BASE}/api/storage/delete`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ fileList }),
    keepalive,
  });
  await readJson(response);
}

export async function checkHealth(): Promise<{ ok: boolean; functions: number; storage?: 'oss' }> {
  const response = await fetch(`${API_BASE}/health`);
  if (!response.ok) throw new ApiError('服务器不可用', response.status);
  return response.json();
}

export function getControlToken(): string {
  return sessionStorage.getItem(CONTROL_TOKEN_KEY) || '';
}

export function clearControlToken(): void {
  sessionStorage.removeItem(CONTROL_TOKEN_KEY);
}

export async function controlLogin(key: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/control/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.code !== 0) throw new ApiError(body.msg || '总管理登录失败', response.status);
  sessionStorage.setItem(CONTROL_TOKEN_KEY, body.data.token);
}

export async function controlRequest<T = unknown>(path: string, options: {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: Record<string, unknown>;
} = {}): Promise<CloudResult<T>> {
  const method = options.method || 'GET';
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${getControlToken()}`,
      ...(method === 'GET' ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(method === 'GET' ? {} : { body: JSON.stringify(options.data || {}) }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.code !== 0) {
    if (response.status === 401) clearControlToken();
    throw new ApiError(body.msg || `请求失败（${response.status}）`, response.status);
  }
  return body;
}

export function enterManagedSystem(adminToken: string, systemId: string): void {
  setToken(adminToken);
  setCurrentSystemId(systemId);
  window.location.href = `/?system=${encodeURIComponent(systemId)}`;
}
